import { Component } from '@theme/component';
import { ThemeEvents, CompareUpdateEvent } from '@theme/events';
import { onAnimationEnd, yieldToMainThread } from '@theme/utilities';

/**
 * @typedef {object} CompareProduct
 * @property {string} id
 * @property {string} handle
 * @property {string} title
 * @property {string} image
 * @property {string} price
 * @property {string} [compareAtPrice]
 */

/**
 * Manages the compare products list in localStorage.
 */
export class ProductCompare {
  /** @static @constant {string} The key used to store the compare list in local storage */
  static #STORAGE_KEY = 'compareProducts';
  /** @static @constant {number} The maximum number of products that can be compared at once */
  static #MAX_PRODUCTS = 4;

  /**
   * Retrieves the list of products saved for comparison.
   * @returns {CompareProduct[]} The list of compare products.
   */
  static getProducts() {
    try {
      return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
    } catch (_error) {
      return [];
    }
  }

  /**
   * @param {string} id - The product ID to check.
   * @returns {boolean} Whether the product is already saved for comparison.
   */
  static isSaved(id) {
    return this.getProducts().some((product) => product.id === id);
  }

  /**
   * Persists the compare list and notifies listeners.
   * @param {CompareProduct[]} products - The updated compare list.
   * @param {'add' | 'remove' | 'clear'} action - What kind of change triggered this update.
   */
  static #save(products, action) {
    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(products));
    document.dispatchEvent(new CompareUpdateEvent(products, action));
  }

  /**
   * Adds or removes a product from the compare list.
   * @param {CompareProduct} data - The product to toggle.
   * @returns {{added: boolean, full: boolean}} Whether the product was added, and whether the list is full.
   */
  static toggleProduct(data) {
    const products = this.getProducts();
    const existingIndex = products.findIndex((product) => product.id === data.id);

    if (existingIndex > -1) {
      products.splice(existingIndex, 1);
      this.#save(products, 'remove');
      return { added: false, full: false };
    }

    if (products.length >= this.#MAX_PRODUCTS) {
      return { added: false, full: true };
    }

    products.push(data);
    this.#save(products, 'add');
    return { added: true, full: false };
  }

  /**
   * Removes a single product from the compare list.
   * @param {string} id - The product ID to remove.
   */
  static removeProduct(id) {
    this.#save(
      this.getProducts().filter((product) => product.id !== id),
      'remove'
    );
  }

  /**
   * Clears the entire compare list.
   */
  static clearProducts() {
    this.#save([], 'clear');
  }
}

/**
 * A toggle button that adds or removes its associated product from the compare list.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} button - The toggle button.
 * @property {HTMLElement} [maxMessage] - The hidden "list is full" message.
 *
 * @extends {Component<Refs>}
 */
export class CompareToggleButton extends Component {
  requiredRefs = ['button'];

  /** @type {number | undefined} */
  #hideMessageTimeout;

  /** @type {number | undefined} */
  #resetAddedTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.#syncState();
    document.addEventListener(ThemeEvents.compareUpdate, this.#syncState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.compareUpdate, this.#syncState);
    clearTimeout(this.#hideMessageTimeout);
    clearTimeout(this.#resetAddedTimeout);
  }

  /**
   * @returns {CompareProduct}
   */
  get #productData() {
    const { productId, productHandle, productTitle, productImage, productPrice, productCompareAtPrice } =
      this.dataset;

    return {
      id: productId ?? '',
      handle: productHandle ?? '',
      title: productTitle ?? '',
      image: productImage ?? '',
      price: productPrice ?? '',
      compareAtPrice: productCompareAtPrice,
    };
  }

  /**
   * Toggles the associated product in/out of the compare list.
   */
  toggle = () => {
    const { added, full } = ProductCompare.toggleProduct(this.#productData);

    if (full) {
      this.#showMaxMessage();
      return;
    }

    if (added && !this.classList.contains('compare-toggle-button--full')) {
      this.#animateFlyToCompareIcon();
      this.#animateAdded();
    }
  };

  #syncState = () => {
    const isSaved = ProductCompare.isSaved(this.dataset.productId ?? '');

    this.refs.button.setAttribute('aria-pressed', String(isSaved));
    this.classList.toggle('compare-toggle-button--active', isSaved);
  };

  #showMaxMessage() {
    const { maxMessage } = this.refs;
    if (!maxMessage) return;

    clearTimeout(this.#hideMessageTimeout);
    maxMessage.classList.add('compare-toggle-button__message--visible');
    this.#hideMessageTimeout = setTimeout(() => {
      maxMessage.classList.remove('compare-toggle-button__message--visible');
    }, 4000);
  }

  /**
   * Sends a copy of the product image flying from this button to the compare
   * icon in the header, mirroring AddToCartComponent#animateFlyToCart in
   * assets/product-form.js.
   */
  #animateFlyToCompareIcon() {
    const compareIcon = document.querySelector('.header-actions__compare-icon');
    const { button } = this.refs;
    const image = this.dataset.productImage;

    if (!compareIcon || !image) return;

    const flyToCartElement = /** @type {HTMLElement & {source: Element, destination: Element}} */ (
      document.createElement('fly-to-cart')
    );

    flyToCartElement.classList.add('fly-to-cart--quick');
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = button;
    flyToCartElement.destination = compareIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Plays the checkmark-burst success state on the button, mirroring
   * AddToCartComponent#animateAddToCart in assets/product-form.js.
   */
  #animateAdded = async () => {
    const { button } = this.refs;

    clearTimeout(this.#resetAddedTimeout);
    button.dataset.added = 'true';

    // onAnimationEnd can trigger a style recalculation, so yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(button);

    this.#resetAddedTimeout = setTimeout(() => {
      delete button.dataset.added;
    }, 800);
  };
}

if (!customElements.get('compare-toggle-button')) {
  customElements.define('compare-toggle-button', CompareToggleButton);
}
